import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import {
  AUTH_ENDPOINTS,
  AUTH_POST_FORGOT_PASSWORD_ENDPOINT,
  AUTH_POST_RESET_PASSWORD_ENDPOINT,
  AUTH_POST_VALIDATE_RESET_PASSWORD_TOKEN_ENDPOINT,
  AUTH_POST_VERIFY_EMAIL_ENDPOINT,
  AUTH_POST_VERIFICATION_EMAIL_ENDPOINT,
  USER_ENDPOINTS,
} from '../api/api-endpoints';
import { TokenService } from './token.service';
import { RestApiService } from '../api/rest-api.service';
import { tap, catchError } from 'rxjs/operators';
import { firstValueFrom, of, Subject, throwError } from 'rxjs';
import { TimezoneService } from './timezone.service';
import { retryTransientRead } from '../api/retry-transient-read';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly oauthLoginRedirectPath = '/auth/login';
  private readonly organisationSwitchedSubject = new Subject<string>();
  readonly organisationSwitched$ = this.organisationSwitchedSubject.asObservable();

  constructor(
    private restApi: RestApiService,
    private tokenService: TokenService,
    private timezoneService: TimezoneService,
    private readonly router: Router,
  ) { }

  register(
    email: string,
    password: string,
    userAttributes: {
      firstName?: String;
      lastName?: String;
    } = {}
  ) {
    return this.restApi.post(AUTH_ENDPOINTS.register, {
      email,
      password,
      userAttributes
    });
  }

  registerInvited(payload: {
    token: string;
    first_name: string;
    last_name: string;
    password: string;
  }) {
    return this.restApi.post(AUTH_ENDPOINTS.register_invited, payload).pipe(
      tap((data: any) => {
        if (data?.access_token && data?.refresh_token) {
          this.tokenService.setTokens(
            data.access_token,
            data.refresh_token,
            true,
            data.expires_at
          );
        }
      })
    );
  }

  login(email: string, password: string, remember: boolean) {
    return this.restApi.post(AUTH_ENDPOINTS.login, {
      username: email,
      password,
      remember_me: remember
    }).pipe(
      tap((data: any) => {
        this.tokenService.setTokens(
          data.access_token,
          data.refresh_token,
          remember,
          data.expires_at
        );
      })
    );
  }

  async refreshToken(accessToken: string | null = this.tokenService.getAccessToken()) {
    try {
      const refreshToken = this.tokenService.getRefreshTokenForAccess(accessToken);
      if (!refreshToken) {
        throw new Error('No refresh token is available for this session.');
      }

      const data: any = await firstValueFrom(
        this.restApi.post(AUTH_ENDPOINTS.refresh, {
          refresh_token: refreshToken
        })
      );

      if (accessToken && this.tokenService.getAccessToken() !== accessToken) {
        return data;
      }

      this.tokenService.setTokens(
        data.access_token,
        data.refresh_token,
        this.tokenService.getRememberForAccess(accessToken) ?? true,
        data.expires_at
      );
      return data;

    } catch (err) {
      if (!isTransientAuthError(err)) {
        this.tokenService.clear(accessToken);
      }
      throw err;
    }
  }

  logout(accessToken: string | null = this.tokenService.getAccessToken()) {
    const refreshToken = this.tokenService.getRefreshTokenForAccess(accessToken);
    this.tokenService.clear(accessToken);

    if (!refreshToken) {
      return of(null);
    }

    return this.restApi
      .post(AUTH_ENDPOINTS.logout, {
        refresh_token: refreshToken
      })
      .pipe(
        tap((_data: any) => {}),
        catchError((err) => {
          this.tokenService.clear(accessToken);
          return throwError(() => err);
        })
      );
  }

  async isLoggedIn() {
    await this.tokenService.hydrateSessionFromOpenTab();
    const accessToken = this.tokenService.getAccessToken();
    if (!accessToken) {
      return false;
    }

    if (!this.tokenService.isTokenExpired()) {
      return true;
    }

    if (!this.tokenService.getRefreshTokenForAccess(accessToken)) {
      this.tokenService.clear(accessToken);
      return false;
    }

    try {
      await this.refreshToken(accessToken);
      return this.tokenService.isLoggedIn() && !this.tokenService.isTokenExpired();
    } catch (error) {
      // An offline backend can interrupt refresh during a restart. Keep a
      // remembered session so the next request can refresh it when available.
      return isTransientAuthError(error)
        && this.tokenService.getAccessToken() === accessToken
        && !!this.tokenService.getRefreshTokenForAccess(accessToken);
    }
  }

  async getUser() {
    try {
      const data: any = await firstValueFrom(
        this.restApi.get(USER_ENDPOINTS.me)
      );
      this.timezoneService.setUserTimezone(data?.timezone);
      return data;
    } catch (err) {
      console.error("user error", err);
      throw err;
    }
  }

  async switchOrganisation(organisationId: string, destination = this.router.url): Promise<void> {
    try {

    let data: any = await firstValueFrom(this.restApi.post(AUTH_ENDPOINTS.switch_organisation, {
      organisation_id: organisationId,
    }));

    this.tokenService.setTokens(
      data.access_token,
      data.refresh_token,
      this.tokenService.getRemember(),
      data.expires_at
    );
    this.organisationSwitchedSubject.next(organisationId);
    // A return URL may originate in a query parameter; never navigate to another origin.
    let safeDestination = '/home';
    try {
      const target = new URL(destination, window.location.origin);
      if (destination.startsWith('/') && target.origin === window.location.origin) {
        safeDestination = `${target.pathname}${target.search}${target.hash}`;
      }
    } catch {
      // Malformed return URLs fall back to Home.
    }
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    if (safeDestination === currentPath) {
      window.location.reload();
    } else {
      window.location.replace(safeDestination);
    }
    } catch (err) {
      console.error("switch organisation error", err);
      throw err;
    }

  }

  async getCurrentOrganisation() {
    try {
      const data: any = await firstValueFrom(
        this.restApi.get(AUTH_ENDPOINTS.current_organisation).pipe(retryTransientRead())
      );
      return data;
    } catch (err) {
      console.error("user error", err);
      throw err;
    }
  }

  // TODO(remove-legacy): compatibility wrappers during organisation migration
  async switchCompany(companyId: string) {
    return this.switchOrganisation(companyId);
  }

  // TODO(remove-legacy): compatibility wrappers during organisation migration
  async getCurrentCompany() {
    return this.getCurrentOrganisation();
  }

  startOAuthLogin(
    provider: 'google' | 'linkedin',
    options?: { returnUrl?: string; rememberMe?: boolean }
  ) {
    const rememberMe = options?.rememberMe ?? true;
    const redirectUrl =
      options?.returnUrl ?? `${window.location.origin}${this.oauthLoginRedirectPath}`;
    const encodedRedirectUrl = encodeURIComponent(redirectUrl);
    const startUrl =
      `${this.restApi.apiURL}/auth/oauth2/${provider}/start` +
      `?redirect_url=${encodedRedirectUrl}&remember_me=${rememberMe ? 'true' : 'false'}`;

    window.location.href = startUrl;
  }

  startGoogleLogin(options?: { returnUrl?: string; rememberMe?: boolean }) {
    this.startOAuthLogin('google', options);
  }

  startLinkedInLogin(options?: { returnUrl?: string; rememberMe?: boolean }) {
    this.startOAuthLogin('linkedin', options);
  }

  completeOAuthLoginFromQuery(params: {
    access_token?: string | null;
    refresh_token?: string | null;
    expires_at?: string | number | null;
    remember_me?: boolean;
  }): boolean {
    const accessToken = `${params?.access_token ?? ''}`.trim();
    const refreshToken = `${params?.refresh_token ?? ''}`.trim();
    const expiresAtRaw = params?.expires_at;
    const rememberMe = params?.remember_me ?? true;
    const expiresAt = Number(expiresAtRaw);

    if (!accessToken || !refreshToken || !Number.isFinite(expiresAt) || expiresAt <= 0) {
      return false;
    }

    this.tokenService.setTokens(accessToken, refreshToken, rememberMe, expiresAt);
    return true;
  }

  setupPassword(payload: { new_password: string; confirm_password: string }) {
    return this.restApi.post(AUTH_ENDPOINTS.set_password, payload);
  }

  forgotPassword(email: string) {
    return this.restApi.post(AUTH_POST_FORGOT_PASSWORD_ENDPOINT, { email });
  }

  resetPassword(payload: { token: string; new_password: string; confirm_password: string }) {
    return this.restApi.post(AUTH_POST_RESET_PASSWORD_ENDPOINT, payload);
  }

  validateResetPasswordToken(token: string) {
    return this.restApi.post(AUTH_POST_VALIDATE_RESET_PASSWORD_TOKEN_ENDPOINT, { token });
  }

  verifyEmail(token: string) {
    return this.restApi.post(AUTH_POST_VERIFY_EMAIL_ENDPOINT, { token });
  }

  resendVerificationEmail(email: string) {
    return this.restApi.post(AUTH_POST_VERIFICATION_EMAIL_ENDPOINT, { email });
  }

}

export function isTransientAuthError(error: unknown): boolean {
  const status = (error as { status?: unknown } | null)?.status;
  return status === 0 || (typeof status === 'number' && status >= 500);
}
